/**
 * English → Simplified Chinese dictionary. The English copy IS the key, so the
 * default (English) render needs no lookup and a missing entry degrades to
 * English instead of a raw key id.
 */
export const ZH: Record<string, string> = {
  "Maximum attempts": "最多尝试次数",
  "Up to {n} attempts": "最多 {n} 次",
  "Stops when a complete rewrite passes basic checks. Extra attempts retry failed sections. Review important details before using the result.": "整篇改写通过基础检查后即结束，后续尝试只重试未通过的段落。使用前仍需核对重要信息。",
  "Basic fidelity check is always on": "基础保真检查始终开启",
  "Passed basic fidelity checks": "已通过基础保真检查",
  "Failed fidelity check": "未通过保真检查",
  "This attempt has no verified rewrite. The original text remains in the input.": "本轮没有通过检查的改写，原文保留在输入框中。",
  "Unsupported characters found. Edit the source before continuing; your original text is preserved.": "发现不支持的字符，请编辑后再继续；原始输入已完整保留。",
  "Paste English text to rewrite. Basic fidelity checks run automatically; review the result for factual accuracy. Your original input is preserved.": "粘贴英文文本开始改写。系统自动进行基础保真检查，仍需核对结果中的事实。原始输入会完整保留。",
  "Your rewrite continues while you visit other tabs. Startup may take a few minutes; completed drafts appear here.": "切换到其他页签后改写会继续。启动可能需要几分钟，完成的草稿会显示在这里。",
  "No faithful rewrite was found; no credits charged": "未生成通过保真检查的结果，本次不扣额度。",
  "Continue payment": "继续支付",
  "Complete your payment": "完成支付",
  "Your payment link is also available in Account → Recent orders.": "也可以在「账户 → 最近订单」中找到支付链接。",
  "Sign in first to purchase credits.": "请先登录再购买额度。",
  "Reset password": "重置密码",
  "Forgot password?": "忘记密码？",
  "If this email has a password account, a reset code has been sent.": "如果此邮箱已注册密码账户，重置验证码已发送。",
  "Password updated. Sign in with your new password; previous sessions have been signed out.": "密码已更新，请使用新密码登录；旧登录会话已失效。",
  "Invalid or expired reset code": "重置验证码无效或已过期",
  "Session expired — please sign in again": "登录已失效，请重新登录",
  "Insufficient available credits — please purchase more first": "可用额度不足，请充值后再试",
  // ---- Landing page -------------------------------------------------------
  "Make AI Sound Human.": "让 AI 写出人味。",
  "HumanInk is a purpose-built humanization model. It rewrites AI-generated text into prose with genuine human texture — while a fidelity guard keeps every number, name and fact intact.":
    "HumanInk 是一个专注文本人化的大模型。它把 AI 生成的文字改写成带有真实人类质感的文章，同时由事实保真守卫确保每个数字、名称与事实分毫不差。",
  "Focused on humanized AI, by design": "专注人化 AI，为此而生",
  "Start Humanizing": "开始人化改写",
  "Contact Us": "联系我们",
  "Launch App": "进入应用",
  Product: "产品",
  "How It Works": "工作原理",
  Contact: "联系方式",
  "Natural wording": "自然表达",
  "Meaning checks": "语义检查",
  "One credit per source word": "每个原文单词消耗 1 额度",
  "Why HumanInk": "为什么选 HumanInk",
  "Rewrite with a natural voice": "让表达更自然",
  "HumanInk uses a model trained for rewriting to adjust sentence rhythm and wording while keeping your message in focus.": "HumanInk 使用专门训练的改写模型，调整句子节奏和措辞，围绕你原本想表达的内容进行改写。",
  "Meaning comes first": "原意优先",
  "Checks look for changes to numbers, percentages, names and meaning. A rewrite that fails the checks is not delivered as a successful result.": "检查会关注数字、百分比、名称和语义的变化。未通过检查的改写不会作为成功结果交付。",
  "Review the result": "审阅改写结果",
  "Compare the rewrite with your source and check the tone, meaning and wording before you use it.": "使用前，请对照原文检查改写的语气、含义和措辞。",
  "Follow the progress": "查看处理进度",
  "Follow the rewrite progress, then review the finished result. Processing time depends on the text and the checks it needs.": "查看改写进度，然后审阅最终结果。处理时间取决于文本和所需的检查。",
  "Three steps to human": "三步完成人化",
  "Paste your text": "粘贴文本",
  "Drop in AI-generated English text — up to 600 words per pass.":
    "粘贴 AI 生成的英文文本，单次最多 600 词。",
  "The model rewrites": "模型改写",
  "The model rewrites your text and checks the result before marking it complete.": "模型改写文本，并在标记完成前检查结果。",
  "Review and copy": "审阅并复制",
  "Review the completed rewrite against your source, then copy the text when you are happy with it.": "将完成的改写与原文对照，确认满意后再复制。",
  "Ready to make your AI text human?": "准备好让你的 AI 文本有人味了吗？",
  "Free credits to start. No credit card required.": "注册即送免费额度，无需信用卡。",
  "All rights reserved.": "保留所有权利。",
  "Vision: make AI human.": "愿景：让 AI 有人味。",

  // ---- App shell -----------------------------------------------------------
  Humanize: "改写",
  "Buy Credits": "购买额度",
  Account: "账户",
  "Not connected": "未连接",
  "Connecting…": "连接中…",
  "Connection failed": "连接失败",
  Connected: "已连接",
  Credits: "额度",
  "Connection settings": "连接设置",
  "API base (empty = same origin)": "服务地址（留空 = 同源）",
  "Iterative rewriting + fidelity guard · usage-based billing":
    "迭代改写 + 事实保真守卫 · 按额度计费",

  // ---- Humanize page -------------------------------------------------------
  "Humanize Text": "文本人化",
  "Paste AI-generated English text and get prose that reads human; the fidelity guard verifies numbers and entities. English only for now — other characters are filtered on input.":
    "粘贴英文 AI 文本，迭代改写到读起来像人；事实保真守卫会校验数字与实体。当前仅支持英文 —— 非英文字符会在输入时自动过滤。",
  "Source (English AI text)": "原文（English AI 文本）",
  "Paste the AI-generated English text to humanize…": "粘贴要人化的英文 AI 文本…",
  "Humanizing…": "改写中…",
  "Humanize Now": "人化改写",
  "over the {n}-word limit": "超出 {n} 词上限",
  "over the {n}-char limit": "超出 {n} 字符上限",
  "Non-English / unsupported characters were removed — English text only for now.":
    "已自动移除非英文/非法字符 —— 目前仅支持英文文本。",
  "Set your API key first (⚙ in the top-right corner)":
    "请先在右上角 ⚙ 设置中填写 API Key",
  "Paste some English text first. Non-English characters are stripped; if nothing remains the request cannot run.":
    "请先粘贴英文原文。非英文字符会被自动去掉，去掉后若变空则无法提交。",
  "Sign up or sign in first — every account gets free words each month.":
    "请先注册或登录 —— 每个账户每月都有免费额度。",
  "New here? Sign up in a minute — 500 free words every month.":
    "第一次来？一分钟注册，每月赠送 500 词免费额度。",
  "Already have an API key? Paste it via ⚙ in the top-right corner.":
    "已有 API Key？点右上角 ⚙ 填入即可。",
  "Sign up / Sign in": "注册 / 登录",
  "Humanized Result": "人化结果",
  "Copy best (draft {n})": "复制最好（第 {n} 稿）",
  "Copy best result": "复制最好结果",
  "Copied ✓": "已复制 ✓",
  Copy: "复制",
  "Result copied to clipboard": "结果已复制到剪贴板",
  "Copy failed — select the text manually": "复制失败，请手动选择文本",
  "Draft {round} / {total} · {chars} chars so far":
    "第 {round} / {total} 稿 · 已生成 {chars} 字",
  "Draft {round} / {total} · {m} parts in parallel · {chars} chars so far":
    "第 {round} / {total} 稿 · {m} 段并行改写 · 已生成 {chars} 字",
  "Request sent, waiting for the first token…": "请求已发出，等待首个字…",
  "In queue — {n} ahead of you, about {s}s to start":
    "排队中 —— 前面还有 {n} 人，预计约 {s} 秒后开始",
  "You're next — waiting for a free slot ({s}s est.)":
    "马上就到你 —— 正在等待空闲算力（预计 {s} 秒）",
  "Waiting for the model…": "等待模型输出…",
  "Draft {n}": "第 {n} 稿",
  Best: "最好",
  "Copy draft {n}": "复制第 {n} 稿",
  "Draft {n} copied": "第 {n} 稿已复制",
  "Copy was blocked by the browser — the text is selected, press ⌘C":
    "浏览器拦了复制，正文已选中，请按 ⌘C",
  "Copy failed — select the text and press ⌘C": "复制失败，请手动选择文本后 ⌘C",
  "Draft {n} not generated": "第 {n} 稿未生成",
  "Streaming starts 1–2 s after you click; finished drafts appear below and can each be copied.":
    "点击后 1–2 秒开始逐字出稿；完成的稿件会陆续出现，都可以复制。",
  "semantic cos {v}": "语义 cos {v}",
  "Checks passed": "检查通过",
  "Fidelity uncertain · double-check numbers/entities": "保真存疑 · 请复核数字/实体",

  // ---- Billing -------------------------------------------------------------
  "1 word = 1 credit — each humanize costs exactly the words you submit. Pay with stablecoins (USDC/USDT) or via Stripe; credits land automatically after payment.":
    "1 词 = 1 额度 —— 每次改写按提交的词数精确扣费。支持稳定币（USDC/USDT）或 Stripe，支付后自动到账。",
  "Payment method": "支付方式",
  "Stripe (Card / Alipay / WeChat Pay)": "Stripe（银行卡 / 支付宝 / 微信）",
  "Pay with Stripe": "用 Stripe 支付",
  Alipay: "支付宝",
  Recommended: "推荐",
  "Pay with stablecoin": "用稳定币支付",
  "Create order & pay": "下单去支付",
  Starter: "入门包",
  Standard: "标准包",
  Pro: "专业包",
  "For trying it out and small batches": "适合试用与小批量改写",
  "Steady daily use at a better rate": "日常稳定使用，单价更优",
  "High volume / teams, lowest rate": "高频/团队，最低单价",
  "Payment received — credits added": "支付成功，额度已到账",
  "Order closed without payment": "订单已关闭，未完成支付",
  "Payment page opened — credits land automatically after you pay":
    "已打开支付页，完成支付后自动到账",
  "No payment link returned — check the order in Account later":
    "未返回支付链接，请稍后在账户中查看订单",
  "No payment address returned — please retry": "未返回收款信息，请稍后重试",
  "This payment channel is not available yet — try another or retry later":
    "该支付渠道暂未开通，请改用其它方式或稍后再试",
  "Order failed, please retry": "下单失败，请重试",
  "Current order": "当前订单",
  "Order #": "订单号 ",
  "Checking…": "查询中…",
  "Waiting for the payment result — this page polls every 2.5 s.":
    "正在等待支付结果，页面会自动更新（每 2.5s 轮询一次）。",
  Created: "已创建",
  Paid: "已支付",
  "Awaiting payment": "等待支付",
  "Payment failed": "支付失败",
  Closed: "已关闭",

  // ---- Crypto pay card -----------------------------------------------------
  "Stablecoin payment": "稳定币付款",
  Received: "已到账",
  "Amount due": "应付",
  Chain: "链",
  "Pay-to address": "收款地址",
  Token: "合约",
  "Time left": "剩余时间",
  Expired: "已过期",
  "Send the exact amount to the address above on {chain} ONLY; a wrong chain / amount / address cannot be recovered.":
    "仅在 {chain} 链向上述地址转账精确金额；转错链 / 错金额 / 错地址将无法找回。",
  "the specified": "指定",
  "Transaction hash 0x…": "转账交易哈希 0x…",
  "I've sent it — verify": "我已转账，提交校验",
  "Enter the transfer transaction hash (tx hash)": "请填写转账交易哈希（tx hash）",
  "Submit failed, please retry": "提交失败，请重试",
  "Confirming {c}/{n} — you can resubmit the same tx later until it lands.":
    "确认中 {c}/{n}，到账前可稍后再次提交同一 tx。",
  "Payment received — tokens credited.": "支付成功，token 已到账。",
  "Order closed — create a new order to pay.": "订单已关闭，请重新下单后再支付。",
  "Cancel": "取消",
  "Back to packages": "返回选择套餐",
  "Order expired — if you already sent the payment, paste the tx hash above and it will still be credited; otherwise cancel and create a new order.":
    "订单已过期 —— 如果你已经转账，把交易哈希粘贴到上方仍可正常入账；如未转账，请取消后重新下单。",
  "Pay-to address copied": "已复制收款地址",
  "Copy failed — select the address manually": "复制失败，请手动选择地址",

  // ---- Account -------------------------------------------------------------
  "Sign in or sign up to manage credits and API keys — or paste an API key via ⚙ in the top-right corner.":
    "登录或注册以管理额度与 API Key；或在右上角 ⚙ 填入 API Key 直接调用。",
  "Credits, usage, orders and API keys.": "额度、用量、订单与 API Key 管理。",
  "Signed in": "已登录",
  "Sign out": "退出登录",
  "Developer mode": "开发者模式",
  "Calling with X-API-Key": "使用 X-API-Key 调用",
  "Sign in / Sign up": "登录 / 注册",
  "Signed up · your API key": "注册成功 · 你的 API Key",
  "Got it": "知道了",
  "This is your account's default key for X-API-Key calls.":
    "这是账户的默认 Key，可用于 X-API-Key 调用。",
  "Total credits": "总额度",
  "free + paid words": "免费 + 付费词数",
  "Free credits": "免费额度",
  "Paid credits": "付费额度",
  "Recent usage": "最近用量",
  Refresh: "刷新",
  "Load failed — refresh to retry.": "加载失败，请刷新重试。",
  "No usage yet.": "暂无用量记录。",
  "{n} chars": "{n} 字",
  Rewrite: "改写",
  "Recent orders": "最近订单",
  "stored on this device": "本设备记录",
  "No orders yet. Orders placed under \"Buy Credits\" appear here.":
    "暂无订单。去「购买额度」下单后会显示在这里。",
  "{n} words": "{n} 词",

  // ---- Keys ----------------------------------------------------------------
  "My API Keys": "我的 API Key",
  "For programmatic calls (X-API-Key)": "用于程序化调用（X-API-Key）",
  "Label (optional, e.g. prod / test)": "标签（可选，如 prod / 测试）",
  "New key": "新建 Key",
  "You won't see this key again after leaving this page; if lost, revoke it and create a new one.":
    "离开本页后将无法再次查看；如遗失请撤销并新建。",
  "Loading…": "加载中…",
  "Load failed — please refresh.": "加载失败，请刷新。",
  "No keys yet — click \"New key\" to create one.": "暂无密钥，点击「新建 Key」创建。",
  Unnamed: "未命名",
  "created {ts}": "创建于 {ts}",
  Revoked: "已撤销",
  Revoke: "撤销",
  "Revoke key \"{label}\"? This cannot be undone.":
    "撤销密钥「{label}」？此操作不可恢复。",
  "Key revoked": "已撤销",
  "Revoke failed": "撤销失败",
  "Create failed": "创建失败",
  "(shown only once — save it now)": "（仅显示一次，请立即保存）",
  Copied: "已复制",
  "Copy failed — select it manually": "复制失败，请手动选择",

  // ---- Auth ----------------------------------------------------------------
  "Email verification code": "邮箱验证码",
  "Send code": "发送验证码",
  "Resend in {s}s": "{s} 秒后可重发",
  "Enter the 6-digit code from your email": "请输入邮件里的 6 位验证码",
  "A code was just sent — check your inbox or wait a minute": "验证码刚发送过，请查收邮箱或稍等一分钟",
  "wrong verification code": "验证码错误",
  "verification code expired — request a new one": "验证码已过期，请重新获取",
  "verification code required": "请先获取邮箱验证码",
  "invalid email address": "邮箱格式不正确",
  "could not send the verification email": "验证码邮件发送失败，请稍后重试",
  or: "或",
  "invalid Google credential": "Google 登录凭证无效，请重试",
  "Google account email is not verified": "该 Google 账户邮箱未验证",
  "Google account has no usable email": "该 Google 账户没有可用邮箱",
  "could not verify the Google sign-in": "无法验证 Google 登录，请稍后重试",
  "google sign-in not enabled": "Google 登录暂未开启",
  "google account already linked": "该 Google 账户已绑定，请重试登录",
  "google account already registered": "该 Google 账户已注册，请重试登录",
  "Sign in to your account": "登录账户",
  "Create an account": "注册账户",
  "Sign in": "登录",
  "Sign up": "注册",
  Email: "邮箱",
  Password: "密码",
  "At least 8 characters": "至少 8 位",
  "You'll get an API key after signing up (shown only once).":
    "注册成功后会发放一个 API Key（仅显示一次）。",
  "Sign up & sign in": "注册并登录",
  "Network error, please retry": "网络错误，请重试",
  "Wrong email or password": "邮箱或密码错误",
  "This email is already registered — sign in instead": "该邮箱已注册，请直接登录",
  "Check the email format and password (min 8 chars)":
    "请检查邮箱格式与密码（密码至少 8 位）",
  "Enter your email": "请输入邮箱",

  // ---- API client / stream errors ------------------------------------------
  "Cannot reach the server — check the API base and your network":
    "无法连接服务，请检查服务地址与网络",
  "Invalid or missing API key — check your settings":
    "API Key 无效或缺失，请检查设置",
  "Insufficient credits — please buy more first": "额度不足，请先购买额度",
  "Not found": "未找到资源",
  "Service busy, please retry shortly": "服务繁忙，请稍后重试",
  "Request failed ({status})": "请求失败（{status}）",
  "The server returned an unrecognized data format": "服务返回了无法识别的数据格式",
  "The server returned no data stream": "服务未返回数据流",
  "Connection interrupted, please retry": "连接中断，请重试",
  "Humanize failed, please retry": "改写失败，请重试",
  // Server-emitted SSE error strings (kept in sync with humanize.rs).
  "The rewrite service is temporarily unavailable, please retry later":
    "改写服务暂不可用，请稍后重试",
  "This request was taken over by a newer retry": "请求已被新的重试接管",
  "Internal error, please retry": "内部错误，请重试",
  "Insufficient credits — please purchase more first": "额度不足，请先购买额度",
  "Billing failed, please retry": "结算失败，请重试",

  // ---- AI detector (POST /detect) -----------------------------------------
  "AI Detector": "AI 检测",
  "AI detector": "AI 检测器",
  "AI detection": "AI 检测",
  "Check {p}": "检查 {p}",
  "Check with {p} · ~{n} words": "用 {p} 检查 · 约 {n} 词",
  "Costs about {n} words": "约消耗 {n} 词额度",
  "Human {n}%": "人类 {n}%",
  cached: "已缓存",
  "Verify on {p} ↗": "在 {p} 官网验证 ↗",
  "Text copied — paste it on {p} to verify": "文本已复制，粘贴到 {p} 即可验证",
  "Detection failed, please retry": "检测失败，请重试",
  "Paste some English text first.": "请先粘贴英文文本。",
  "Paste any English text and see how an AI detector scores it, sentence by sentence. If it reads as AI, one click sends it to the humanizer.":
    "粘贴任意英文文本，看 AI 检测器如何逐句打分。如果被判为 AI，一键送去人化改写。",
  "Text to check": "待检测文本",
  "Paste the English text to check…": "粘贴要检测的英文文本…",
  "Detection result": "检测结果",
  "Sentence breakdown": "逐句分析",
  "Likely AI": "疑似 AI",
  Uncertain: "不确定",
  "Likely human": "疑似人类",
  "Humanize this text →": "把它改写成人类 →",
  "Already reads as human — humanize anyway": "已经像人写的了，仍要改写",
  "Score the text on the left to see the result here.": "对左侧文本执行检测后，结果会显示在这里。",
  "The text changed since this score — check again for a current result.":
    "文本在这次评分之后被修改过，重新检查以获得当前结果。",
  "Detector scores are a snapshot: detectors retrain, so a result carries the date it was taken.":
    "检测分数是一个快照：检测器会持续重训，所以每个结果都带有评分日期。",
  "Turnitin's AI report is visible only to instructors. No third-party tool can check it — including any that put the name on a button.":
    "Turnitin 的 AI 报告只有老师能看到。没有任何第三方工具能替你检查——包括那些把它的名字写在按钮上的。",
  "Detection is not configured on this server.": "此服务器未配置检测器。",
  "Verdict: {v}": "判定：{v}",
};
